import React, { useEffect, useRef, useMemo } from "react";
import { Container } from "@pixi/react";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../pixiSettings";
import OverlayReelSet from "../OverlayReelSet/OverlayReelSet";
import WinText from "../../../Components/Animations/WinText/WinText";
import { WIN_TEXT_CONFIG } from "../../../Components/Animations/WinText/WinTextController";
import ReelFront from "../../../Components/Animations/ReelFront/ReelFront";
import ReelBack from "../../../Components/Animations/ReelBack/ReelBack";
import ReelBackDecoration from "../../../Components/Animations/ReelBackDecoration/ReelBackDecoration";
import WinLinesContainer from "../../Shared/WinLinesReelSet/WinLinesContainer";
import WinBoxContainer from "../../Shared/WinBoxReelSet/WinBoxContainer";
import DimReelSet from "../../Shared/DimReelSet/DimReelSet";
import DividerReelset from "../../Shared/DividerReelset/DividerReelset";
import ReelSet from "../ReelSet/ReelSet";
import MiniPaytable from "../../Shared/MiniPaytable/MiniPaytable";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import { Settings } from "../../../settings";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
import {
  useSlot,
  getCounterScale,
} from "../../../Contexts/SlotRegistryContext";
import TensionReelSet from "../TensionReelSet/TensionReelSet";
import RoyalCollectGameContainer from "../../RoyalCollect/RoyalCollectGameContainer";
import { useSelector } from "react-redux";
import {
  selectHoldAndWinActive,
  selectFreeRoundsActive,
  selectFreeRoundsEnded,
  selectFreeRoundsCompletionPending,
  selectErrorRecoveryTransitionActive,
  selectErrorRecoveryScreenCovered,
  selectIntroPopupActive,
  selectMenuActive,
} from "../../../State/selectors";
import FreeRoundsCounter from "../../../Components/UserInterface/secondary/Displays/FreeRoundsCounter";
import FreeSpinSceneTransition from "../../../Components/Animations/FreeSpin/FreeSpinSceneTransition/FreeSpinSceneTransition";
import FreeSpinReelSet from "../../FreeSpin/FreeSpinReelSet/FreeSpinReelSet";
import FreeSpinOverlayReelset from "../../FreeSpin/FreeSpinOverlayReelset/FreeSpinOverlayReelset";
import FreeSpinMaskReelSet from "../../FreeSpin/FreeSpinMaskReelSet/FreeSpinMaskReelSet";
import Jackpots from "../../../Components/Animations/Jackpots/Jackpots";
import LogoLandscape from "../../../Components/UserInterface/shared/Logos/LogoLandscape";
import LogoPortrait from "../../../Components/UserInterface/shared/Logos/LogoPortrait";

// Game container Y offset config (multiplied by symbolHeight, subtracted from center)
// Positive values push the reels UP from center
// Exported so BackgroundAnimationController can follow same offsets
export const GAME_CONTAINER_OFFSET_CONFIG = {
  [PixiSettings.DEVICE.MOBILE_SMALL]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: 0.9,
    [PixiSettings.ORIENTATION.LANDSCAPE]: 0.055,
  },
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: 0.9,
    [PixiSettings.ORIENTATION.LANDSCAPE]: 0.055,
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: 0.6,
    [PixiSettings.ORIENTATION.LANDSCAPE]: 0.0,
  },
  [PixiSettings.DEVICE.DESKTOP]: 0.2,
};

const GameContainer = () => {
  const {
    dimensions,
    symbolWidth,
    symbolHeight,
    symbolScale,
    dividerDimensions,
    reelSpacing,
    orientation,
    device,
    isMobileSmall,
  } = useDimensions();

  const {
    dimReelSetRef,
    winLinesContainerRef,
    winBoxContainerRef,
    globalLineWinTextRef,
    lineWinTextRef,
  } = useReelRefs();
  const holdAndWinActive = useSelector(selectHoldAndWinActive);

  const freeRoundsActive = useSelector(selectFreeRoundsActive);
  const freeRoundsEnded = useSelector(selectFreeRoundsEnded);
  const freeRoundsCompletionPending = useSelector(
    selectFreeRoundsCompletionPending,
  );
  const errorRecoveryTransitionActive = useSelector(
    selectErrorRecoveryTransitionActive,
  );
  const errorRecoveryScreenCovered = useSelector(
    selectErrorRecoveryScreenCovered,
  );
  const introPopupActive = useSelector(selectIntroPopupActive);
  const menuActive = useSelector(selectMenuActive);
  const containerRef = useRef(null);
  const reelContainerRef = useRef(null);

  // Memoize container position calculations
  // Use real device (isMobileSmall) to resolve MOBILE_SMALL offset config
  const containerY = useMemo(() => {
    const realDevice = isMobileSmall
      ? PixiSettings.DEVICE.MOBILE_SMALL
      : device;
    const deviceConfig = GAME_CONTAINER_OFFSET_CONFIG[realDevice];
    const offsetMultiplier =
      typeof deviceConfig === "number"
        ? deviceConfig
        : (deviceConfig?.[orientation] ?? 0.325);
    return dimensions.height / 2 - symbolHeight * offsetMultiplier;
  }, [dimensions.height, symbolHeight, device, orientation, isMobileSmall]);

  const containerX = useMemo(() => dimensions.width / 2, [dimensions.width]);

  // Counter-scale to undo the spine's inherited scale on the reel_container.
  // Only needed when reelset is injected into a spine bone (bone injection mode).
  const spineCounterScale = useMemo(
    () => getCounterScale(symbolScale),
    [symbolScale],
  );

  // Memoize Free Rounds display condition
  const shouldShowFreeRoundsCounter = useMemo(
    () =>
      FEATURES_CONFIG.FREE_ROUNDS.ENABLED &&
      !freeRoundsEnded &&
      (freeRoundsActive || freeRoundsCompletionPending) &&
      !holdAndWinActive &&
      (!errorRecoveryTransitionActive || errorRecoveryScreenCovered),
    [
      freeRoundsEnded,
      freeRoundsActive,
      freeRoundsCompletionPending,
      holdAndWinActive,
      errorRecoveryTransitionActive,
      errorRecoveryScreenCovered,
    ],
  );

  // Inject reel_container into background spine's base_reel slot via SlotRegistry
  // Replaces checkAndAttach polling — useSlot re-renders when slot becomes available
  const baseReelSlot = useSlot("background.base_reel");
  useEffect(() => {
    if (!FEATURES_CONFIG.BACKGROUND.REELSET_BONE_INJECTION) return;
    if (!baseReelSlot || !reelContainerRef.current) return;
    const node = reelContainerRef.current;
    baseReelSlot.addChild(node);
    return () => {
      if (node.parent === baseReelSlot) {
        baseReelSlot.removeChild(node);
      }
    };
  }, [baseReelSlot]);

  // Update container position when dimensions change
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.y = containerY;
    }
  }, [dimensions.width, dimensions.height, symbolHeight, containerY]);

  // Apply counter-scale to reel_container (bone injection mode only)
  useEffect(() => {
    if (!FEATURES_CONFIG.BACKGROUND.REELSET_BONE_INJECTION) return;
    if (reelContainerRef.current) {
      reelContainerRef.current.scale.set(spineCounterScale);
    }
  }, [spineCounterScale]);

  return (
    <Container
      ref={containerRef}
      name="game_container"
      x={containerX}
      y={containerY}
      sortableChildren
      eventMode="passive"
      zIndex={5}
    >
      {/* Inner container for shakeable elements (reels, symbols, etc.) */}
      <Container
        ref={reelContainerRef}
        name="reel_container"
        x={0}
        y={0}
        alpha={introPopupActive || (menuActive && Settings.APP.UI_THEME === "primary") ? 0.001 : 1}
        sortableChildren
        eventMode="passive"
      >
        {/* Event animations (event_in → event_idle) behind reel frame, after free spin transition */}
        {FEATURES_CONFIG.FREE_SPINS.ENABLED && (
          <ReelBackDecoration
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
            dimensions={dimensions}
            dividerDimensions={dividerDimensions}
          />
        )}

        <ReelBack
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          dimensions={dimensions}
          dividerDimensions={dividerDimensions}
        />

        <ReelSet
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          dividerDimensions={dividerDimensions}
          reelSpacing={reelSpacing}
        />

        {FEATURES_CONFIG.FREE_SPINS.ENABLED && (
          <FreeSpinReelSet
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
            dividerDimensions={dividerDimensions}
            reelSpacing={reelSpacing}
          />
        )}

        <DimReelSet
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          dividerDimensions={dividerDimensions}
          ref={dimReelSetRef}
        />

        <ReelFront
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          dimensions={dimensions}
          dividerDimensions={dividerDimensions}
        />

        {FEATURES_CONFIG.DIVIDERS.ENABLED && (
          <DividerReelset
            symbolHeight={symbolHeight}
            dividerDimensions={dividerDimensions}
            reelSpacing={reelSpacing}
          />
        )}

        {FEATURES_CONFIG.WIN_BOX.ENABLED && (
          <WinBoxContainer
            ref={winBoxContainerRef}
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
            reelSpacing={reelSpacing}
          />
        )}

        <OverlayReelSet
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          reelSpacing={reelSpacing}
        />

        {FEATURES_CONFIG.FREE_SPINS.ENABLED && <FreeSpinOverlayReelset />}

        {FEATURES_CONFIG.FREE_SPINS.ENABLED && <FreeSpinMaskReelSet />}

        {/* Unified WinText components */}
        {FEATURES_CONFIG.WIN_LINE_DIGIT.ENABLED && (
          <Container name="win_text_overlay" zIndex={6}>
            {/* Global win text - larger font, shown during "show all wins" phase */}
            <WinText
              ref={globalLineWinTextRef}
              fontSizeMultiplier={WIN_TEXT_CONFIG.GLOBAL_WIN_FONT_SIZE}
              hideOnBigWin={true}
            />

            {/* Line win text - smaller font, shown during individual line cycling */}
            <WinText
              ref={lineWinTextRef}
              fontSizeMultiplier={WIN_TEXT_CONFIG.LINE_WIN_FONT_SIZE}
              hideOnBigWin={true}
            />
          </Container>
        )}

        {FEATURES_CONFIG.WIN_LINES.ENABLED && (
          <WinLinesContainer
            ref={winLinesContainerRef}
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
          />
        )}

        {FEATURES_CONFIG.TENSION.ENABLED && !holdAndWinActive && (
          <TensionReelSet />
        )}

        {FEATURES_CONFIG.MINI_PAYTABLE.ENABLED && <MiniPaytable />}
      </Container>

      {/* Jackpots bar — injected into reel_front spine's jackpots_position slot via SlotRegistry */}
      {FEATURES_CONFIG.JACKPOTS.ENABLED && <Jackpots />}

      {/* Base logos — injected into background spine bone slots via SlotRegistry */}
      {!introPopupActive && !(menuActive && Settings.APP.UI_THEME === "primary") && (
        <>
          <LogoLandscape target="base" renderMode="spine" />
          <LogoPortrait target="base" renderMode="spine" />
        </>
      )}

      {/* Royal Collection — separate container attached to feature_reel spine slot */}
      {FEATURES_CONFIG.ROYAL_COLLECT.ENABLED && <RoyalCollectGameContainer />}

      {/* Free Rounds Counter - positioned at bottom center of reels */}
      {shouldShowFreeRoundsCounter && <FreeRoundsCounter />}

      {/* Free Spin Scene Transition — inside game_container, below UI */}
      {FEATURES_CONFIG.FREE_SPINS.ENABLED && <FreeSpinSceneTransition />}
    </Container>
  );
};

export default GameContainer;
